/**
 * usePopup Hook - Centralized Popup State Management
 *
 * Provides consistent hover popup behavior across the app:
 * - Configurable show/hide delays
 * - Global coordination (only one popup visible at a time)
 * - Clean timeout management
 */

import { useState, useCallback, useRef, useEffect } from 'react';

// Global registry to track the currently active popup
let activePopupId: string | null = null;
let activePopupCloseCallback: (() => void) | null = null;

// Generate unique IDs for popup instances
let popupIdCounter = 0;
function generatePopupId(): string {
  return `popup-${++popupIdCounter}`;
}

export interface UsePopupOptions {
  /** Delay before showing popup (ms). Default: 100 */
  showDelay?: number;
  /** Delay before hiding popup (ms). Default: 150 */
  hideDelay?: number;
  /** If true, this popup won't close other popups when opening. Default: false */
  allowMultiple?: boolean;
}

export interface UsePopupReturn {
  /** Whether the popup is currently visible */
  isVisible: boolean;
  /** Call when mouse enters the trigger element */
  onMouseEnter: () => void;
  /** Call when mouse leaves the trigger element */
  onMouseLeave: () => void;
  /** Call when mouse enters the popup itself (to keep it open) */
  onPopupMouseEnter: () => void;
  /** Call when mouse leaves the popup */
  onPopupMouseLeave: () => void;
  /** Manually show the popup */
  show: () => void;
  /** Manually hide the popup */
  hide: () => void;
  /** Props to spread on the trigger element */
  triggerProps: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  };
  /** Props to spread on the popup element */
  popupProps: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  };
}

export function usePopup(options: UsePopupOptions = {}): UsePopupReturn {
  const { showDelay = 100, hideDelay = 150, allowMultiple = false } = options;

  const [isVisible, setIsVisible] = useState(false);
  const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const popupIdRef = useRef<string>(generatePopupId());

  // Cleanup timeouts on unmount
  useEffect(() => {
    const popupId = popupIdRef.current;
    return () => {
      if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
      // If this popup was active, clear the global state
      if (activePopupId === popupId) {
        activePopupId = null;
        activePopupCloseCallback = null;
      }
    };
  }, []);

  const show = useCallback(() => {
    // Cancel any pending hide
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    // If already visible, nothing to do
    if (isVisible) return;

    // Cancel any pending show (to reset the timer)
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
    }

    showTimeoutRef.current = setTimeout(() => {
      // Close any other active popup (unless allowMultiple)
      if (!allowMultiple && activePopupId && activePopupId !== popupIdRef.current) {
        activePopupCloseCallback?.();
      }

      // Register this popup as active
      activePopupId = popupIdRef.current;
      activePopupCloseCallback = () => setIsVisible(false);

      setIsVisible(true);
    }, showDelay);
  }, [isVisible, showDelay, allowMultiple]);

  const hide = useCallback(() => {
    // Cancel any pending show
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }

    // If already hidden, nothing to do
    if (!isVisible) return;

    // Cancel any pending hide (to reset the timer)
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      // Clear global state if this was the active popup
      if (activePopupId === popupIdRef.current) {
        activePopupId = null;
        activePopupCloseCallback = null;
      }
    }, hideDelay);
  }, [isVisible, hideDelay]);

  const onMouseEnter = useCallback(() => {
    show();
  }, [show]);

  const onMouseLeave = useCallback(() => {
    hide();
  }, [hide]);

  const onPopupMouseEnter = useCallback(() => {
    // Cancel any pending hide when entering the popup
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const onPopupMouseLeave = useCallback(() => {
    hide();
  }, [hide]);

  return {
    isVisible,
    onMouseEnter,
    onMouseLeave,
    onPopupMouseEnter,
    onPopupMouseLeave,
    show,
    hide,
    triggerProps: {
      onMouseEnter,
      onMouseLeave,
    },
    popupProps: {
      onMouseEnter: onPopupMouseEnter,
      onMouseLeave: onPopupMouseLeave,
    },
  };
}
